// Medio campo de fútbol sala con los 5 slots de la formación (portero,
// cierre, 2 alas, pívot). Cada slot es un círculo clicable que muestra el
// jugador asignado (o un hueco vacío) y abre el selector al pulsarlo. En
// móvil se muestra en vertical (portería arriba) y en escritorio en
// horizontal (portería a la izquierda), igual que la pista de volley.
import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

export default function MedioCampoFutsal({ slots, asignaciones, jugadoresById, onSlotClick }) {
  const theme = useTheme();
  const esHorizontal = useMediaQuery(theme.breakpoints.up("sm"));

  const jugadorEnSlot = (slotId) => {
    const entrada = asignaciones.find((a) => a.slot === slotId);
    return entrada?.jugador_id ? jugadoresById[entrada.jugador_id] : null;
  };

  // Coordenadas canónicas pensadas en horizontal (portería a la izquierda).
  // En vertical se transponen para que la portería quede arriba.
  const posicion = (slot) => (esHorizontal ? { x: slot.x, y: slot.y } : { x: slot.y, y: slot.x });

  return (
    <Box
      sx={{
        position: "relative",
        // En vertical (móvil) se limita la altura para que quepa sin scroll;
        // el ancho lo calcula el navegador a partir del aspect-ratio.
        width: esHorizontal ? "100%" : "auto",
        height: esHorizontal ? "auto" : "38vh",
        maxWidth: "100%",
        mx: esHorizontal ? 0 : "auto",
        aspectRatio: esHorizontal ? "16 / 10" : "10 / 16",
        borderRadius: 3,
        overflow: "hidden",
        background: "linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.15)",
      }}
    >
      {/* Líneas de una pista de fútbol sala: área curva (no el rectángulo de
          fútbol) formada por dos cuartos de círculo desde cada palo, unidos
          por un tramo recto, más los dos puntos de penalti (6m y 10m). */}
      <Box
        component="svg"
        viewBox={esHorizontal ? "0 0 100 62.5" : "0 0 62.5 100"}
        preserveAspectRatio="none"
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      >
        <g
          fill="none"
          stroke="rgba(255,255,255,0.6)"
          strokeWidth="0.6"
          vectorEffect="non-scaling-stroke"
        >
          {esHorizontal ? (
            <>
              {/* Contorno */}
              <rect x="1" y="1" width="98" height="60.5" rx="1.5" />
              {/* Portería */}
              <line x1="1" y1="24" x2="1" y2="38.5" strokeWidth="1.4" />
              {/* Área (dos cuartos de círculo unidos por un tramo recto) */}
              <path d="M 1 11 A 13 13 0 0 1 14 24 L 14 38.5 A 13 13 0 0 1 1 51.5" />
              {/* Puntos de penalti (6m y 10m) */}
              <circle cx="14" cy="31.25" r="0.6" fill="rgba(255,255,255,0.6)" />
              <circle cx="23" cy="31.25" r="0.6" fill="rgba(255,255,255,0.6)" />
              {/* Cuarto de círculo de medio campo (línea derecha) */}
              <path d="M 99 1 A 30 30 0 0 1 99 61.5" />
            </>
          ) : (
            <>
              {/* Contorno */}
              <rect x="1" y="1" width="60.5" height="98" rx="1.5" />
              {/* Portería */}
              <line x1="24" y1="1" x2="38.5" y2="1" strokeWidth="1.4" />
              {/* Área (dos cuartos de círculo unidos por un tramo recto) */}
              <path d="M 11 1 A 13 13 0 0 0 24 14 L 38.5 14 A 13 13 0 0 0 51.5 1" />
              {/* Puntos de penalti (6m y 10m) */}
              <circle cx="31.25" cy="14" r="0.6" fill="rgba(255,255,255,0.6)" />
              <circle cx="31.25" cy="23" r="0.6" fill="rgba(255,255,255,0.6)" />
              {/* Cuarto de círculo de medio campo (línea inferior) */}
              <path d="M 1 99 A 30 30 0 0 0 61.5 99" />
            </>
          )}
        </g>
      </Box>

      {/* Slots de jugadores */}
      {slots.map((slot) => {
        const jugador = jugadorEnSlot(slot.id);
        const pos = posicion(slot);
        return (
          <Tooltip key={slot.id} title={jugador ? jugador.nombre : `Asignar ${slot.label}`}>
            <Box
              onClick={(e) => onSlotClick(slot, e)}
              sx={{
                position: "absolute",
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: { xs: 38, sm: 46 },
                  height: { xs: 38, sm: 46 },
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  color: jugador ? "#1B2A55" : "#fff",
                  bgcolor: jugador ? "#fff" : alpha("#fff", 0.18),
                  border: "2px solid",
                  borderColor: jugador ? "#fff" : alpha("#fff", 0.55),
                  boxShadow: jugador ? "0 2px 6px rgba(0,0,0,0.35)" : "none",
                  transition: "transform .15s ease",
                  "&:hover": { transform: "scale(1.08)" },
                }}
              >
                {jugador ? jugador.numero ?? jugador.nombre?.[0] ?? "?" : "+"}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: { xs: "0.6rem", sm: "0.68rem" },
                  textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                  maxWidth: 72,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {jugador ? jugador.nombre.split(" ")[0] : slot.label}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

MedioCampoFutsal.propTypes = {
  slots: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    })
  ).isRequired,
  asignaciones: PropTypes.arrayOf(
    PropTypes.shape({ slot: PropTypes.string, jugador_id: PropTypes.string })
  ).isRequired,
  jugadoresById: PropTypes.object.isRequired,
  onSlotClick: PropTypes.func.isRequired,
};
